import React from 'react';

import { PageProps } from '../../../components/types/coretypes';
import { FWorkbenchShell } from '../workbench';

//import axios, { AxiosInstance } from 'axios';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';

import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';

import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';

import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';

import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';

import {
  BieRecordId,
  IBieApiResponse,
  IBieOnboardingValidationIssue,
  getBieImportClassLabel,
  isBieFlagEnabled,
} from './bieonboardingworkbench.models';

//import { IBieOnboardingImportRecord } from './BieOnboardingImportRecords';
import { IBieOnboardingImportRecord } from './bieonboarding.import.models';

import BieOnboardingStatusChip from './BieOnboardingStatusChip';
import { normalizeImportItemId } from './bieonboarding.normalize';
import { fetchBieOnboardingImportItem, fetchBieOnboardingValidationIssues } from './bieonboardingworkbench.apis';
import { IBieOnboardingWorkbenchProps } from './BieOnboardingWorkbench';

/*
 * Adjust this path to the shared Axios client used by the
 * application.
 */
//import apiClient from '../../../services/apiClient';

/* ============================================================
 * API configuration
 * ============================================================
 */

//const DEFAULT_IMPORT_ITEM_ENDPOINT = '/api/bie/onboarding/import-items';

// const DEFAULT_VALIDATION_ISSUES_ENDPOINT =
//   '/api/bie/onboarding/validation/issues';

/* ============================================================
 * Types
 * ============================================================
 */

export type BieOnboardingImportItemTab =
  | 'OVERVIEW'
  | 'SOURCE'
  | 'VALIDATION'
  | 'ISSUES';

const PAGE_REVIEW_WORKBENCH = 'bieonboardingreview';
const PAGE_IMPORT_RECORDS = 'bieonboardingimport';

export interface IBieOnboardingImportItemProps extends PageProps {
  importItemId?: BieRecordId;

  //httpClient?: AxiosInstance;

  /**
   * Base endpoint for a single staged record.
   *
   * Default request:
   *
   * GET /api/bie/onboarding/import-items/:importItemId
   */
  importItemEndpoint?: string;

  /**
   * Default:
   *
   * GET /api/bie/onboarding/validation/issues
   */
  validationIssuesEndpoint?: string;

  onBack?: () => void;

  onOpenImportRecords?: (importId: BieRecordId) => void;

  onOpenWorkbench?: (loadSessionId: BieRecordId) => void;
}

interface IBieOnboardingImportItemState {
  loading: boolean;
  refreshing: boolean;

  errorMessage: string;

  selectedTab: BieOnboardingImportItemTab;

  item: IBieOnboardingImportRecord | null;

  issues: IBieOnboardingValidationIssue[];
}

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Detailed view of one staged BIE onboarding Import Item.
 *
 * Presents:
 *
 * - Source workbook lineage
 * - Import and Load Session identity
 * - Validation and resolution state
 * - Metric, Product, Entity, Location, Account, Period and Unit
 * - Raw workbook record
 * - Validation details
 * - Validation issues generated against the row
 */
export default class BieOnboardingImportItem extends React.Component<
  IBieOnboardingImportItemProps,
  IBieOnboardingImportItemState
> {
  //private readonly httpClient: AxiosInstance;

  private mounted = false;

  private requestVersion = 0;

  public constructor(props: IBieOnboardingImportItemProps) {
    super(props);

    //this.httpClient = props.httpClient || apiClient;

    this.state = {
      loading: false,
      refreshing: false,

      errorMessage: '',

      selectedTab: 'OVERVIEW',

      item: null,
      issues: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;

    void this.loadImportItem(true);
  }

  public componentDidUpdate(
    previousProps: IBieOnboardingImportItemProps
  ): void {
    if (
      String(this.resolveImportItemId(previousProps)) !==
      String(this.resolveImportItemId(this.props))
    ) {
      this.requestVersion += 1;

      this.setState(
        {
          loading: false,
          refreshing: false,

          errorMessage: '',

          selectedTab: 'OVERVIEW',

          item: null,
          issues: [],
        },
        () => {
          void this.loadImportItem(true);
        }
      );
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    this.requestVersion += 1;
  }

  public render(): React.ReactNode {
    const { loading, item } = this.state;

    return (
      <FWorkbenchShell
        //title={this.props.title || 'Onboarding Import Item'}
        title={'Institutional Onboarding Workbench - Import Item'}
        subtitle={item ? `${item.importCode} · Row ${item.sourceRow}` : 'Inspect one staged onboarding record.'}
        icon={<DataObjectOutlinedIcon />}
        loading={loading && !item}
        error={this.state.errorMessage}
        onBack={this.handleBack}
        onRefresh={this.handleRefresh}
      >
        {loading && !item ? (
          this.renderLoading()
        ) : !item ? (
          this.renderUnavailable()
        ) : (
          <Stack spacing={2}>
            {this.state.refreshing && <LinearProgress />}
            {this.renderHeader()}
            {this.renderTabs()}
            {this.renderSelectedTab()}
          </Stack>
        )}
      </FWorkbenchShell>
    );
  }

  private resolveImportItemId(
    props: IBieOnboardingImportItemProps = this.props
  ): BieRecordId {
    return props.importItemId ?? props.params?.importitemid ?? '';
  }

  private resolveLoadSessionId(
      props: IBieOnboardingWorkbenchProps = this.props
    ): BieRecordId {
      return props.loadSessionId ?? props.params?.loadsessionid ?? '';
    }

  private readonly handleBack = (): void => {
    if (this.props.onBack) {
      this.props.onBack();
      return;
    }

    const setParam = this.props.onParam('set');
    setParam('loadsessionid', this.props.params?.loadsessionid || this.state.item?.loadSessionId || null);
    setParam('importid', this.props.params?.importid || this.state.item?.importId || null);
    setParam('stp', this.props.params?.stp || this.state.item?.stamp || null);
    this.props.setPage(PAGE_IMPORT_RECORDS);
  };

  /* ==========================================================
   * Header
   * ==========================================================
   */

  private renderHeader(): React.ReactNode {
    const item = this.requireItem();

    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
          },

          position: 'relative',
          overflow: 'hidden',

          '&::before': {
            content: '""',

            position: 'absolute',
            top: 0,
            left: 0,

            width: 5,
            height: '100%',

            backgroundColor: (theme) => {
              switch (String(item.validationCode).toUpperCase()) {
                case 'VALID':
                  return theme.palette.success.main;

                case 'WARNING':
                  return theme.palette.warning.main;

                case 'FAILED':
                  return theme.palette.error.main;

                default:
                  return theme.palette.info.main;
              }
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              md: 'flex-start',
            }}
            spacing={2}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{
                minWidth: 0,
              }}
            >
              {this.props.onBack && (
                <Tooltip title="Back" arrow>
                  <IconButton
                    disabled={this.isBusy()}
                    onClick={this.props.onBack}
                  >
                    <ArrowBackOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Box
                sx={{
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Typography variant="h5" fontWeight={700}>
                    Staged Record
                  </Typography>

                  <BieOnboardingStatusChip
                    kind="VALIDATION_STATE"
                    value={item.validationCode}
                    label={item.validationName || undefined}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    color={item.isResolved ? 'success' : 'warning'}
                    icon={
                      item.isResolved ? (
                        <CheckCircleOutlineIcon />
                      ) : (
                        <LinkOffOutlinedIcon />
                      )
                    }
                    label={item.isResolved ? 'Resolved' : 'Unresolved'}
                  />
                </Stack>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.65,
                  }}
                >
                  {item.sourceSheet || 'Unknown Sheet'}
                  {' · Row '}
                  {this.formatNumber(item.sourceRow)}
                  {item.sourceReference ? ` · ${item.sourceReference}` : ''}
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{
                    mt: 1,
                  }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getBieImportClassLabel(item.classCode)}
                  />

                  {item.recordClassCode && (
                    <Chip
                      size="small"
                      label={
                        item.recordClassName ||
                        this.formatCodeLabel(item.recordClassCode)
                      }
                    />
                  )}

                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Item ${String(item.rid)}`}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{
                flexShrink: 0,
              }}
            >
              {item.loadSessionId && this.props.onOpenWorkbench && (
                <Button
                  variant="outlined"
                  disabled={this.isBusy()}
                  startIcon={<AssessmentOutlinedIcon />}
                  onClick={this.handleOpenWorkbench}
                >
                  Workbench
                </Button>
              )}

              {item.importId && this.props.onOpenImportRecords && (
                <Button
                  variant="outlined"
                  disabled={this.isBusy()}
                  startIcon={<DatasetOutlinedIcon />}
                  onClick={this.handleOpenImportRecords}
                >
                  Import Records
                </Button>
              )}

              <Tooltip title="Refresh staged record" arrow>
                <span>
                  <IconButton
                    disabled={this.isBusy()}
                    onClick={this.handleRefresh}
                  >
                    {this.state.refreshing ? (
                      <CircularProgress size={22} />
                    ) : (
                      <RefreshOutlinedIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs:12, sm:6, lg:3 }}>
              {this.renderHeaderMetadata(
                'Load Session',
                item.loadSessionName || item.loadSessionCode || '—',
                item.loadSessionCode
              )}
            </Grid>

            <Grid size={{ xs:12, sm:6, lg:3 }}>
              {this.renderHeaderMetadata(
                'Import',
                item.importName || item.importCode || '—',
                item.importCode
              )}
            </Grid>

            <Grid size={{ xs:12, sm:6, lg:3 }}>
              {this.renderHeaderMetadata(
                'Validation Issues',
                this.formatNumber(item.issueCount),
                `${this.formatNumber(item.blockingIssueCount)} blocking`
              )}
            </Grid>

            <Grid size={{ xs:12, sm:6, lg:3 }}>
              {this.renderHeaderMetadata(
                'Concurrency Stamp',
                item.stamp || '—',
                ''
              )}
            </Grid>
          </Grid>
        </Stack>
      </Paper>
    );
  }

  private renderHeaderMetadata(
    label: string,
    value: React.ReactNode,
    supportingValue?: React.ReactNode
  ): React.ReactNode {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>

        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            mt: 0.25,
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>

        {supportingValue && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.2,
              overflowWrap: 'anywhere',
            }}
          >
            {supportingValue}
          </Typography>
        )}
      </Box>
    );
  }

  /* ==========================================================
   * Tabs
   * ==========================================================
   */

  private renderTabs(): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={this.state.selectedTab}
          onChange={this.handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,

            '& .MuiTab-root': {
              minHeight: 56,
              fontWeight: 600,
            },
          }}
        >
          <Tab
            value="OVERVIEW"
            icon={<AssessmentOutlinedIcon />}
            iconPosition="start"
            label="Overview"
          />

          <Tab
            value="SOURCE"
            icon={<DataObjectOutlinedIcon />}
            iconPosition="start"
            label="Source Data"
          />

          <Tab
            value="VALIDATION"
            icon={<FactCheckOutlinedIcon />}
            iconPosition="start"
            label="Validation"
          />

          <Tab
            value="ISSUES"
            icon={<RuleOutlinedIcon />}
            iconPosition="start"
            label={`Issues (${this.formatNumber(this.state.issues.length)})`}
          />
        </Tabs>
      </Paper>
    );
  }

  private renderSelectedTab(): React.ReactNode {
    switch (this.state.selectedTab) {
      case 'SOURCE':
        return this.renderSourceTab();

      case 'VALIDATION':
        return this.renderValidationTab();

      case 'ISSUES':
        return this.renderIssuesTab();

      case 'OVERVIEW':
      default:
        return this.renderOverviewTab();
    }
  }

  /* ==========================================================
   * Overview tab
   * ==========================================================
   */

  private renderOverviewTab(): React.ReactNode {
    const item = this.requireItem();

    return (
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs:12, lg:8 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.25,
                height: '100%',
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                Resolved Business References
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.4,
                }}
              >
                Controlled BIE and enterprise references resolved from the
                workbook record.
              </Typography>

              <Divider
                sx={{
                  my: 2,
                }}
              />

              <Grid container spacing={1.5}>
                <Grid size={{ xs:12, sm:6, md:4 }}>
                  {this.renderReferenceCard(
                    'Metric / Driver',
                    item.driverCode,
                    item.driverName,
                    item.driverId
                  )}
                </Grid>

                <Grid size={{ xs:12, sm:6, md:4 }}>
                  {this.renderReferenceCard(
                    'Product',
                    item.productCode,
                    item.productName,
                    item.productId
                  )}
                </Grid>

                <Grid size={{ xs:12, sm:6, md:4 }}>
                  {this.renderReferenceCard(
                    'Analytical Entity',
                    item.entityCode,
                    item.entityName,
                    item.entityId
                  )}
                </Grid>

                <Grid size={{ xs:12, sm:6, md:4 }}>
                  {this.renderReferenceCard(
                    'Location',
                    item.locationCode,
                    item.locationName,
                    item.locationId
                  )}
                </Grid>

                <Grid size={{ xs:12, sm:6, md:4 }}>
                  {this.renderReferenceCard(
                    'Account',
                    item.accountCode,
                    item.accountName,
                    item.accountId
                  )}
                </Grid>

                <Grid size={{ xs:12, sm:6, md:4 }}>
                  {this.renderReferenceCard(
                    'Unit',
                    item.unitCode,
                    item.unitName,
                    item.unitId
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs:12, lg:4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.25,
                height: '100%',
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                Record Value and Period
              </Typography>

              <Divider
                sx={{
                  my: 2,
                }}
              />

              <Stack spacing={2}>
                {this.renderValueBlock(
                  'Source Value',
                  this.formatSourceValue(item.sourceValue)
                )}

                {this.renderValueBlock(
                  'Unit',
                  item.unitCode || item.unitName || '—'
                )}

                {this.renderValueBlock(
                  'Period Date',
                  this.formatDate(item.periodDate)
                )}

                {this.renderValueBlock('Period Code', item.periodCode || '—')}

                {this.renderValueBlock(
                  'Record Class',
                  item.recordClassName || item.recordClassCode || '—'
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {this.renderIssueSummary()}
      </Stack>
    );
  }

  private renderReferenceCard(
    label: string,
    code: string,
    name: string,
    resolvedId: BieRecordId | null | undefined
  ): React.ReactNode {
    const hasSourceReference = Boolean(code || name);

    const resolved =
      resolvedId !== null &&
      resolvedId !== undefined &&
      String(resolvedId).trim() !== '';

    return (
      <Card
        variant="outlined"
        sx={{
          height: '100%',

          borderColor:
            hasSourceReference && !resolved ? 'warning.main' : 'divider',
        }}
      >
        <CardContent
          sx={{
            p: 1.5,

            '&:last-child': {
              pb: 1.5,
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              {label}
            </Typography>

            {hasSourceReference && (
              <Chip
                size="small"
                variant="outlined"
                color={resolved ? 'success' : 'warning'}
                label={resolved ? 'Resolved' : 'Unresolved'}
              />
            )}
          </Stack>

          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              mt: 1,
              overflowWrap: 'anywhere',
            }}
          >
            {name || code || 'Not applicable'}
          </Typography>

          {code && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontFamily="monospace"
              sx={{
                display: 'block',
                mt: 0.25,
              }}
            >
              {code}
            </Typography>
          )}

          {resolved && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: 'block',
                mt: 0.5,
              }}
            >
              Record ID {String(resolvedId)}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  private renderValueBlock(
    label: string,
    value: React.ReactNode
  ): React.ReactNode {
    return (
      <Box
        sx={{
          p: 1.4,

          borderRadius: 1.5,

          border: '1px solid',

          borderColor: 'divider',

          backgroundColor: 'action.hover',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Typography
          variant="body1"
          fontWeight={700}
          sx={{
            mt: 0.25,
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  }

  private renderIssueSummary(): React.ReactNode {
    const item = this.requireItem();

    const hasBlockingIssues = item.blockingIssueCount > 0;

    const hasWarnings = item.warningIssueCount > 0;

    return (
      <Alert
        severity={
          hasBlockingIssues
            ? 'error'
            : hasWarnings
              ? 'warning'
              : item.issueCount > 0
                ? 'info'
                : 'success'
        }
        icon={
          hasBlockingIssues ? (
            <ErrorOutlineIcon />
          ) : item.issueCount === 0 ? (
            <CheckCircleOutlineIcon />
          ) : undefined
        }
        action={
          item.issueCount > 0 ? (
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                this.setState({
                  selectedTab: 'ISSUES',
                })
              }
            >
              View Issues
            </Button>
          ) : undefined
        }
      >
        {item.issueCount === 0
          ? 'No active validation issues are attached to this ' +
            'staged record.'
          : `${this.formatNumber(
              item.issueCount
            )} issue(s) are attached to this record, including ` +
            `${this.formatNumber(item.blockingIssueCount)} blocking issue(s).`}
      </Alert>
    );
  }

  /* ==========================================================
   * Source tab
   * ==========================================================
   */

  private renderSourceTab(): React.ReactNode {
    const item = this.requireItem();

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs:12, lg:8 }}>
          {this.renderJsonPanel(
            'Raw Workbook Record',
            'Values extracted directly from the institution workbook.',
            item.rawData,
            <DataObjectOutlinedIcon />
          )}
        </Grid>

        <Grid size={{ xs:12, lg:4 }}>
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SourceOutlinedIcon color="primary" />

                <Typography variant="h6" fontWeight={700}>
                  Source Lineage
                </Typography>
              </Stack>

              <Divider
                sx={{
                  my: 2,
                }}
              />

              <Stack spacing={1.5}>
                {this.renderLineageField('Worksheet', item.sourceSheet)}

                {this.renderLineageField(
                  'Source Row',
                  this.formatNumber(item.sourceRow)
                )}

                {this.renderLineageField(
                  'Source Reference',
                  item.sourceReference
                )}

                {this.renderLineageField('Import Code', item.importCode)}

                {this.renderLineageField('Load Session', item.loadSessionCode)}

                {this.renderLineageField(
                  'Record Hash',
                  this.readRawValue(
                    item.source,
                    'recordhash',
                    'recordHash',
                    'rhh'
                  )
                )}
              </Stack>
            </Paper>

            {this.renderJsonPanel(
              'Additional Information',
              'Supplementary metadata retained against the staged record.',
              item.extraInfo,
              <DatasetOutlinedIcon />
            )}
          </Stack>
        </Grid>
      </Grid>
    );
  }

  private renderLineageField(label: string, value: unknown): React.ReactNode {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            mt: 0.2,
            overflowWrap: 'anywhere',
          }}
        >
          {value === null || value === undefined || String(value).trim() === ''
            ? '—'
            : String(value)}
        </Typography>
      </Box>
    );
  }

  /* ==========================================================
   * Validation tab
   * ==========================================================
   */

  private renderValidationTab(): React.ReactNode {
    const item = this.requireItem();

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs:12, lg:8 }}>
          {this.renderJsonPanel(
            'Validation Details',
            'Resolution and validation diagnostics generated by the Pack 5 engine.',
            item.validationDetails,
            <FactCheckOutlinedIcon />
          )}
        </Grid>

        <Grid size={{ xs:12, lg:4 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              height: '100%',
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Validation Result
            </Typography>

            <Divider
              sx={{
                my: 2,
              }}
            />

            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  Validation State
                </Typography>

                <Box
                  sx={{
                    mt: 0.75,
                  }}
                >
                  <BieOnboardingStatusChip
                    kind="VALIDATION_STATE"
                    value={item.validationCode}
                    label={item.validationName || undefined}
                  />
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  Resolution
                </Typography>

                <Box
                  sx={{
                    mt: 0.75,
                  }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    color={item.isResolved ? 'success' : 'warning'}
                    icon={
                      item.isResolved ? (
                        <CheckCircleOutlineIcon />
                      ) : (
                        <LinkOffOutlinedIcon />
                      )
                    }
                    label={
                      item.resolutionStatus ||
                      (item.isResolved ? 'RESOLVED' : 'UNRESOLVED')
                    }
                  />
                </Box>
              </Box>

              {this.renderCountBox('Total Issues', item.issueCount, 'default')}

              {this.renderCountBox(
                'Blocking Issues',
                item.blockingIssueCount,
                'error'
              )}

              {this.renderCountBox(
                'Warnings',
                item.warningIssueCount,
                'warning'
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  private renderCountBox(
    label: string,
    value: number,
    tone: 'default' | 'warning' | 'error'
  ): React.ReactNode {
    return (
      <Box
        sx={{
          p: 1.4,

          borderRadius: 1.5,

          border: '1px solid',

          borderColor:
            tone === 'error'
              ? 'error.main'
              : tone === 'warning'
                ? 'warning.main'
                : 'divider',

          backgroundColor:
            tone === 'error'
              ? 'error.main' + '0A'
              : tone === 'warning'
                ? 'warning.main' + '0A'
                : 'action.hover',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Typography
          variant="h5"
          fontWeight={700}
          color={
            tone === 'error'
              ? 'error.main'
              : tone === 'warning'
                ? 'warning.main'
                : 'text.primary'
          }
          sx={{
            mt: 0.25,
          }}
        >
          {this.formatNumber(value)}
        </Typography>
      </Box>
    );
  }

  /* ==========================================================
   * Issues tab
   * ==========================================================
   */

  private renderIssuesTab(): React.ReactNode {
    const { issues } = this.state;

    if (issues.length === 0) {
      return (
        <Paper
          variant="outlined"
          sx={{
            py: 7,
            px: 3,
            textAlign: 'center',
          }}
        >
          <CheckCircleOutlineIcon
            color="success"
            sx={{
              fontSize: 54,
            }}
          />

          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mt: 1,
            }}
          >
            No validation issues
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.75,
            }}
          >
            No active validation issues are attached to this Import Item.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Validation Issues
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Rule findings generated against this staged record.
          </Typography>
        </Box>

        <Divider />

        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1050,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Severity</TableCell>

                <TableCell>Rule</TableCell>

                <TableCell>Field</TableCell>

                <TableCell>Message</TableCell>

                <TableCell>Source Value</TableCell>

                <TableCell>Impact</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {issues.map((issue) => (
                <TableRow key={String(issue.rid)} hover>
                  <TableCell>
                    <BieOnboardingStatusChip
                      kind="SEVERITY"
                      value={issue.svc}
                      blocking={issue.ibl}
                    />
                  </TableCell>

                  <TableCell>
                    <Stack spacing={0.2}>
                      <Typography variant="body2" fontWeight={700}>
                        {issue.rln || issue.rlc}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontFamily="monospace"
                      >
                        {issue.rlc}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {issue.fld || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 420,
                      }}
                    >
                      {issue.msg || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{
                        maxWidth: 200,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {issue.src || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={isBieFlagEnabled(issue.ibl) ? 'error' : 'info'}
                      label={
                        issue.ict ||
                        (isBieFlagEnabled(issue.ibl) ? 'BLOCKING' : 'REVIEW')
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  /* ==========================================================
   * JSON panel
   * ==========================================================
   */

  private renderJsonPanel(
    title: string,
    description: string,
    value: Record<string, unknown> | null | undefined,
    icon: React.ReactElement
  ): React.ReactNode {
    const hasValue = value && Object.keys(value).length > 0;

    return (
      <Paper
        variant="outlined"
        sx={{
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                color: 'primary.main',

                display: 'flex',

                mt: 0.25,
              }}
            >
              {icon}
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                {title}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        <Box
          component="pre"
          sx={{
            m: 0,

            p: 2,

            minHeight: 320,
            maxHeight: 620,

            overflow: 'auto',

            backgroundColor: 'action.hover',

            fontSize: 12,
            lineHeight: 1.55,

            whiteSpace: 'pre-wrap',

            overflowWrap: 'anywhere',
          }}
        >
          {hasValue ? JSON.stringify(value, null, 2) : 'No data available.'}
        </Box>
      </Paper>
    );
  }

  /* ==========================================================
   * Loading
   * ==========================================================
   */

  private renderLoading(): React.ReactNode {
    return (
      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Box
                sx={{
                  width: '60%',
                }}
              >
                <Skeleton variant="text" width="45%" height={40} />

                <Skeleton variant="text" width="70%" />

                <Skeleton variant="text" width="55%" />
              </Box>

              <Skeleton variant="rounded" width={150} height={38} />
            </Stack>

            <Divider />

            <Grid container spacing={2}>
              {Array.from({
                length: 4,
              }).map((_, index) => (
                <Grid size={{ xs:12, sm:6, lg:3 }} key={index}>
                  <Skeleton variant="rounded" height={60} />
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Paper>

        <Skeleton variant="rounded" height={56} />

        <Grid container spacing={2}>
          <Grid size={{ xs:12, lg:8 }}>
            <Skeleton variant="rounded" height={420} />
          </Grid>

          <Grid size={{ xs:12, lg:4 }}>
            <Skeleton variant="rounded" height={420} />
          </Grid>
        </Grid>
      </Stack>
    );
  }

  private renderUnavailable(): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
        }}
      >
        <DatasetOutlinedIcon
          color="disabled"
          sx={{
            fontSize: 56,
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mt: 1,
          }}
        >
          Staged record unavailable
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
          }}
        >
          The selected onboarding Import Item could not be loaded.
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{
            mt: 2,
          }}
        >
          {this.props.onBack && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={this.props.onBack}
            >
              Back
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<RefreshOutlinedIcon />}
            onClick={this.handleRefresh}
          >
            Retry
          </Button>
        </Stack>
      </Paper>
    );
  }

  /* ==========================================================
   * Data loading
   * ==========================================================
   */

  private async loadImportItem(initialLoad = false): Promise<void> {
    const requestVersion = ++this.requestVersion;

    this.setState({
      loading: initialLoad,
      refreshing: !initialLoad,

      errorMessage: '',
    });

    try {


      // const importItemId = this.requirePositiveId(
      //   this.props.importItemId,
      //   'Import Item ID'
      // );

      // const itemEndpoint =
      //   this.props.importItemEndpoint || DEFAULT_IMPORT_ITEM_ENDPOINT;

      // const issuesEndpoint =
      //   this.props.validationIssuesEndpoint ||
      //   DEFAULT_VALIDATION_ISSUES_ENDPOINT;

      // const [itemResponse, issuesResponse] = await Promise.all([
      //   this.httpClient.get<IBieApiResponse<Record<string, unknown>>>(
      //     `${itemEndpoint}/${encodeURIComponent(importItemId)}`
      //   ),

      //   this.httpClient.get<IBieApiResponse<IBieOnboardingValidationIssue[]>>(
      //     issuesEndpoint,
      //     {
      //       params: {
      //         importItemId,

      //         status: 1,

      //         pageOffset: 0,
      //         pageLimit: 1000,
      //       },
      //     }
      //   ),
      // ]);

      // if (!this.mounted || requestVersion !== this.requestVersion) {
      //   return;
      // }

      // if (
      //   !itemResponse.data ||
      //   itemResponse.data.success !== true ||
      //   !itemResponse.data.data
      // ) {
      //   throw new Error(
      //     itemResponse.data?.message ||
      //       'The Import Item request was not successful.'
      //   );
      // }

      // if (!issuesResponse.data || issuesResponse.data.success !== true) {
      //   throw new Error(
      //     issuesResponse.data?.message ||
      //       'The validation issue request was not successful.'
      //   );
      // }

      // const item = this.normalizeRecord(itemResponse.data.data);

      // const issues = Array.isArray(issuesResponse.data.data)
      //   ? issuesResponse.data.data
      //   : [];

      // fetch here

      const importItemId = this.resolveImportItemId(); // 
      const loadSessionId = this.resolveLoadSessionId();
      const request = {
        importItemId,
        loadSessionId,
        status: 1,
        pageOffset: 0, //this.state.pageOffset,
        pageLimit: 1000, //this.state.pageLimit,
      };

      const [item, issues] =  await Promise.all([
        await fetchBieOnboardingImportItem(importItemId),
        await fetchBieOnboardingValidationIssues(request),
      ]);

      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        item,
        issues,

        loading: false,
        refreshing: false,

        errorMessage: '',
      });
    } catch (error) {
      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        loading: false,
        refreshing: false,

        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  /* ==========================================================
   * Event handlers
   * ==========================================================
   */

  private readonly handleRefresh = (): void => {
    void this.loadImportItem(false);
  };

  private readonly handleTabChange = (
    _event: React.SyntheticEvent,
    value: BieOnboardingImportItemTab
  ): void => {
    this.setState({
      selectedTab: value,
    });
  };

  private readonly handleOpenImportRecords = (): void => {
    const item = this.state.item;

    if (!item) {
      return;
    }

    if (this.props.onOpenImportRecords) {
      this.props.onOpenImportRecords(item.importId);
      return;
    }

    const setParam = this.props.onParam('set');
    setParam('loadsessionid', item.loadSessionId || this.props.params?.loadsessionid || null);
    setParam('importid', item.importId);
    setParam('stp', item.stamp || this.props.params?.stp || null);
    this.props.setPage(PAGE_IMPORT_RECORDS);
  };

  private readonly handleOpenWorkbench = (): void => {
    const item = this.state.item;

    if (!item?.loadSessionId) {
      return;
    }

    if (this.props.onOpenWorkbench) {
      this.props.onOpenWorkbench(item.loadSessionId);
      return;
    }

    const setParam = this.props.onParam('set');
    setParam('loadsessionid', item.loadSessionId);
    setParam('stp', this.props.params?.stp || null);
    this.props.setPage(PAGE_REVIEW_WORKBENCH);
  };

  // /* ==========================================================
  //  * Record normalization
  //  * ==========================================================
  //  */

  // private normalizeRecord(
  //   source: Record<string, unknown>
  // ): IBieOnboardingImportRecord {
  //   const read = (...keys: string[]): unknown => this.readValue(source, keys);

  //   const recordId = this.readRequiredId(read('rid', 'recid', 'id'));

  //   const resolutionStatus = this.readText(
  //     read('resolutionstatus', 'resolutionStatus', 'rst')
  //   ).toUpperCase();

  //   const explicitResolved = read('isresolved', 'isResolved', 'irs');

  //   const isResolved =
  //     explicitResolved !== null && explicitResolved !== undefined
  //       ? this.readBoolean(explicitResolved)
  //       : resolutionStatus === 'RESOLVED';

  //   return {
  //     rid: recordId,

  //     rix: this.readText(read('rix', 'recix')),

  //     importId: this.readId(read('importid', 'importId', 'imi')) || 0,

  //     importIx: this.readText(read('importix', 'importIx', 'imx')),

  //     importCode: this.readText(read('importcode', 'importCode', 'imc')),

  //     importName: this.readText(read('importname', 'importName', 'imn')),

  //     loadSessionId: this.readId(read('loadsessionid', 'loadSessionId', 'lsi')),

  //     loadSessionCode: this.readText(
  //       read('loadsessioncode', 'loadSessionCode', 'lsc')
  //     ),

  //     loadSessionName: this.readText(
  //       read('loadsessionname', 'loadSessionName', 'lsn')
  //     ),

  //     classCode: this.readText(
  //       read('classcode', 'classCode', 'clc')
  //     ).toUpperCase(),

  //     className: this.readText(read('classname', 'className', 'cln')),

  //     recordClassId: this.readId(read('recordclassid', 'recordClassId', 'rci')),

  //     recordClassCode: this.readText(
  //       read('recordclasscode', 'recordClassCode', 'rcc')
  //     ).toUpperCase(),

  //     recordClassName: this.readText(
  //       read('recordclassname', 'recordClassName', 'rcn')
  //     ),

  //     sourceSheet: this.readText(read('sourcesheet', 'sourceSheet', 'ssh')),

  //     sourceRow: this.readNumber(read('sourcerow', 'sourceRow', 'srw')),

  //     sourceReference: this.readText(
  //       read('sourcereference', 'sourceReference', 'srf')
  //     ),

  //     validationStateId: this.readId(
  //       read('validationstateid', 'validationStateId', 'vsi')
  //     ),

  //     validationCode: this.readText(
  //       read('validationcode', 'validationCode', 'vsc', 'vlc')
  //     ).toUpperCase(),

  //     validationName: this.readText(
  //       read('validationname', 'validationName', 'vsn', 'vln')
  //     ),

  //     resolutionStatus:
  //       resolutionStatus || (isResolved ? 'RESOLVED' : 'UNRESOLVED'),

  //     isResolved,

  //     driverId: this.readId(read('driverid', 'driverId', 'dri')),

  //     driverCode: this.readText(
  //       read('drivercode', 'driverCode', 'drc', 'metriccode', 'metricCode')
  //     ),

  //     driverName: this.readText(
  //       read('drivername', 'driverName', 'drn', 'metricname', 'metricName')
  //     ),

  //     productId: this.readId(read('productid', 'productId', 'pdi')),

  //     productCode: this.readText(read('productcode', 'productCode', 'pdc')),

  //     productName: this.readText(read('productname', 'productName', 'pdn')),

  //     entityId: this.readId(read('entityid', 'entityId', 'eni')),

  //     entityCode: this.readText(read('entitycode', 'entityCode', 'enc')),

  //     entityName: this.readText(read('entityname', 'entityName', 'enn')),

  //     locationId: this.readId(read('locationid', 'locationId', 'lci')),

  //     locationCode: this.readText(read('locationcode', 'locationCode', 'lcc')),

  //     locationName: this.readText(read('locationname', 'locationName', 'lcn')),

  //     accountId: this.readId(read('accountid', 'accountId', 'aci')),

  //     accountCode: this.readText(read('accountcode', 'accountCode', 'acc')),

  //     accountName: this.readText(read('accountname', 'accountName', 'acn')),

  //     periodId: this.readId(read('periodid', 'periodId', 'pei')),

  //     periodCode: this.readText(read('periodcode', 'periodCode', 'pec')),

  //     periodDate: this.readNullableText(
  //       read('perioddate', 'periodDate', 'ped')
  //     ),

  //     unitId: this.readId(read('unitid', 'unitId', 'uni')),

  //     unitCode: this.readText(read('unitcode', 'unitCode', 'unc')),

  //     unitName: this.readText(read('unitname', 'unitName', 'unn')),

  //     sourceValue: this.readSourceValue(
  //       read('sourcevalue', 'sourceValue', 'val')
  //     ),

  //     issueCount: this.readNumber(read('issuecount', 'issueCount', 'isc')),

  //     blockingIssueCount: this.readNumber(
  //       read('blockingissuecount', 'blockingIssueCount', 'bic')
  //     ),

  //     warningIssueCount: this.readNumber(
  //       read('warningissuecount', 'warningIssueCount', 'wic')
  //     ),

  //     rawData: this.readObject(read('rawdata', 'rawData', 'rwd')),

  //     validationDetails: this.readObject(
  //       read('validationdetails', 'validationDetails', 'vdd')
  //     ),

  //     extraInfo: this.readObject(read('extrainfo', 'extraInfo', 'exi')),

  //     status: this.readNumber(read('status', 'sts')),

  //     stamp: this.readText(read('stamp', 'stp')),

  //     source,
  //   };
  // }

  // /* ==========================================================
  //  * Helpers
  //  * ==========================================================
  //  */

  private requireItem(): IBieOnboardingImportRecord {
    if (!this.state.item) {
      throw new Error('Onboarding Import Item data is required.');
    }

    return this.state.item;
  }

  private isBusy(): boolean {
    return this.state.loading || this.state.refreshing;
  }

  // private requirePositiveId(value: unknown, fieldName: string): string {
  //   const normalized = String(value ?? '').trim();

  //   if (!normalized || !/^\d+$/.test(normalized)) {
  //     throw new Error(`${fieldName} must be a positive integer.`);
  //   }

  //   if (BigInt(normalized) <= BigInt(0)) {
  //     throw new Error(`${fieldName} must be greater than zero.`);
  //   }

  //   return normalized;
  // }

  // private readRequiredId(value: unknown): BieRecordId {
  //   const normalized = this.requirePositiveId(value, 'Import Item ID');

  //   const numericValue = Number(normalized);

  //   return Number.isSafeInteger(numericValue) ? numericValue : normalized;
  // }

  // private readId(value: unknown): BieRecordId | null {
  //   if (value === null || value === undefined || String(value).trim() === '') {
  //     return null;
  //   }

  //   const normalized = String(value).trim();

  //   if (!/^\d+$/.test(normalized)) {
  //     return null;
  //   }

  //   const numericValue = Number(normalized);

  //   return Number.isSafeInteger(numericValue) ? numericValue : normalized;
  // }

  private readValue(source: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        return source[key];
      }

      const matchedKey = Object.keys(source).find(
        (candidate) => candidate.toLowerCase() === key.toLowerCase()
      );

      if (matchedKey) {
        return source[matchedKey];
      }
    }

    return undefined;
  }

  private readRawValue(
    source: Record<string, unknown>,
    ...keys: string[]
  ): string {
    const value = this.readValue(source, keys);

    return this.readText(value);
  }

  private readText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  // private readNullableText(value: unknown): string | null {
  //   const normalized = this.readText(value);

  //   return normalized || null;
  // }

  // private readNumber(value: unknown): number {
  //   const normalized = Number(value);

  //   return Number.isFinite(normalized) ? normalized : 0;
  // }

  // private readBoolean(value: unknown): boolean {
  //   if (value === true || value === 1 || value === '1') {
  //     return true;
  //   }

  //   const normalized = String(value ?? '')
  //     .trim()
  //     .toLowerCase();

  //   return (
  //     normalized === 'true' || normalized === 'yes' || normalized === 'resolved'
  //   );
  // }

  // private readObject(value: unknown): Record<string, unknown> {
  //   if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
  //     return value as Record<string, unknown>;
  //   }

  //   if (typeof value === 'string' && value.trim()) {
  //     try {
  //       const parsed = JSON.parse(value);

  //       if (
  //         typeof parsed === 'object' &&
  //         parsed !== null &&
  //         !Array.isArray(parsed)
  //       ) {
  //         return parsed as Record<string, unknown>;
  //       }
  //     } catch {
  //       return {};
  //     }
  //   }

  //   return {};
  // }

  // private readSourceValue(value: unknown): string | number | null {
  //   if (value === null || value === undefined || value === '') {
  //     return null;
  //   }

  //   if (typeof value === 'number') {
  //     return value;
  //   }

  //   const normalized = String(value).trim();

  //   const numericValue = Number(normalized);

  //   return Number.isFinite(numericValue) ? numericValue : normalized;
  // }

  private getErrorMessage(error: unknown): string {
    // if (axios.isAxiosError(error)) {
    //   const response = error.response?.data as
    //     | {
    //         error?: {
    //           message?: string;
    //         };

    //         message?: string;
    //       }
    //     | undefined;

    //   return (
    //     response?.error?.message ||
    //     response?.message ||
    //     error.message ||
    //     'Unable to retrieve the staged Import Item.'
    //   );
    // }

    if (error instanceof Error) {
      return error.message;
    }

    return (
      'An unexpected error occurred while retrieving the ' +
      'staged Import Item.'
    );
  }

  private formatCodeLabel(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  private formatNumber(value: unknown): string {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return new Intl.NumberFormat().format(numericValue);
  }

  private formatSourceValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
      return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 6,
      }).format(numericValue);
    }

    return String(value);
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }

    const normalized = String(value).trim();

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!match) {
      return normalized;
    }

    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );

    if (Number.isNaN(date.getTime())) {
      return normalized;
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(date);
  }
}
